import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    Clock,
    Zap,
    Type,
    Sparkles,
    Power,
    MessageCircle
} from 'lucide-react';

function ProactiveBubbleSettings({
    isEditMode,
    updateConfig,
    proactiveBubbleEnabled = true,
    setProactiveBubbleEnabled,
    proactiveBubbleDelay = 5,
    setProactiveBubbleDelay,
    proactiveBubbleMode = 'ai',
    setProactiveBubbleMode,
    proactiveBubbleDefaultMessage = 'Have a question? Ask me anything!',
    setProactiveBubbleDefaultMessage,
}) {
    // Local state for smooth interactions
    const [enabled, setEnabled] = useState(proactiveBubbleEnabled);
    const [delay, setDelay] = useState(proactiveBubbleDelay);
    const [mode, setMode] = useState(proactiveBubbleMode);
    const [message, setMessage] = useState(proactiveBubbleDefaultMessage);

    // Sync from props when they change (external updates or initial load)
    useEffect(() => {
        setEnabled(proactiveBubbleEnabled);
        setDelay(proactiveBubbleDelay);
        setMode(proactiveBubbleMode);
        setMessage(proactiveBubbleDefaultMessage);
    }, [proactiveBubbleEnabled, proactiveBubbleDelay, proactiveBubbleMode, proactiveBubbleDefaultMessage]);


    const persistAndUpdate = useCallback((partialConfig) => {
        if (!isEditMode) {
            const savedConfig = JSON.parse(localStorage.getItem('chatbotConfig')) || {};
            const newConfig = { ...savedConfig, ...partialConfig };
            localStorage.setItem('chatbotConfig', JSON.stringify(newConfig));
        }
        updateConfig(partialConfig);
    }, [updateConfig, isEditMode]);

    const handleEnabledChange = (checked) => {
        setEnabled(checked);
        if (setProactiveBubbleEnabled) setProactiveBubbleEnabled(checked);
        persistAndUpdate({ proactiveBubbleEnabled: checked });
    };

    const handleDelayChange = (value) => {
        const numValue = Number(value);
        setDelay(numValue);
        if (setProactiveBubbleDelay) setProactiveBubbleDelay(numValue);
        persistAndUpdate({ proactiveBubbleDelay: numValue });
    };

    const handleModeChange = (value) => {
        setMode(value);
        if (setProactiveBubbleMode) setProactiveBubbleMode(value);
        persistAndUpdate({ proactiveBubbleMode: value });
    };

    const handleMessageChange = (value) => {
        setMessage(value);
        if (setProactiveBubbleDefaultMessage) setProactiveBubbleDefaultMessage(value);
        persistAndUpdate({ proactiveBubbleDefaultMessage: value });
    };

    return (
        <div className="flex h-full w-full bg-slate-50/50 p-3 gap-3 overflow-hidden">
            <div className="w-full h-full flex flex-col bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
                {/* Header */}
                <div className="p-4 border-b border-slate-100 bg-white sticky top-0 z-10 flex items-center justify-between">
                    <div>
                        <h2 className="text-lg font-bold tracking-tight text-slate-900">Proactive Bubble</h2>
                        <p className="text-xs text-slate-500 font-medium mt-0.5">Engage visitors before they start a conversation.</p>
                    </div>
                </div>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto custom-scrollbar">
                    <div className="max-w-3xl mx-auto px-8 py-8 space-y-10">

                        {/* 1. Master Toggle */}
                        <div className="bg-slate-50 rounded-xl border border-slate-100 p-5 flex items-center justify-between group hover:border-slate-200 transition-colors">
                            <div className="flex items-start gap-4">
                                <div className={`p-2.5 rounded-lg transition-colors ${enabled ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-400'}`}>
                                    <Power size={20} />
                                </div>
                                <div className="space-y-1">
                                    <h3 className="text-base font-semibold text-slate-900">Enable Proactive Bubble</h3>
                                    <p className="text-sm text-slate-500">Show a welcoming message popup above the chat widget.</p>
                                </div>
                            </div>
                            <button
                                onClick={() => handleEnabledChange(!enabled)}
                                className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 ${enabled ? 'bg-blue-600' : 'bg-slate-200'}`}
                            >
                                <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                            </button>
                        </div>

                        {enabled && (
                            <div className="space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-500">

                                {/* 2. Timing (Delay) */}
                                <div className="space-y-5">
                                    <div className="flex items-start gap-3">
                                        <div className="mt-0.5">
                                            <Clock size={18} className="text-slate-400" />
                                        </div>
                                        <div className="space-y-1 flex-1">
                                            <div className="flex items-center justify-between">
                                                <label className="block text-sm font-semibold text-slate-900">Appearance Delay</label>
                                                <span className="text-xs font-mono text-slate-500 bg-slate-100 px-2 py-0.5 rounded">{delay}s</span>
                                            </div>
                                            <p className="text-xs text-slate-500">Time to wait after page load before showing the message.</p>
                                        </div>
                                    </div>

                                    <div className="pl-8">
                                        <div className="relative pt-6 pb-2">
                                            <input
                                                type="range"
                                                min="1"
                                                max="60"
                                                step="1"
                                                value={delay}
                                                onChange={(e) => handleDelayChange(e.target.value)}
                                                className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-slate-900"
                                            />
                                            <div className="flex justify-between mt-2 text-[10px] text-slate-400 font-medium uppercase tracking-wider">
                                                <span>Immediate (1s)</span>
                                                <span>Standard (5s)</span>
                                                <span>Patient (60s)</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <hr className="border-slate-100" />

                                {/* 3. Message Strategy (Mode) */}
                                <div className="space-y-5">
                                    <div className="flex items-start gap-3">
                                        <div className="mt-0.5">
                                            <Zap size={18} className="text-slate-400" />
                                        </div>
                                        <div className="space-y-1">
                                            <label className="block text-sm font-semibold text-slate-900">Message Strategy</label>
                                            <p className="text-xs text-slate-500">Choose how the greeting message is generated.</p>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-8">
                                        {/* AI Mode Option */}
                                        <div
                                            onClick={() => handleModeChange('ai')}
                                            className={`relative cursor-pointer rounded-xl border-2 p-4 transition-all duration-200 ${mode === 'ai'
                                                ? 'border-slate-900 bg-slate-900/5'
                                                : 'border-slate-100 bg-white hover:border-slate-200 hover:bg-slate-50'
                                                }`}
                                        >
                                            <div className="flex items-center justify-between mb-3">
                                                <div className={`p-2 rounded-lg ${mode === 'ai' ? 'bg-white text-slate-900 shadow-sm' : 'bg-slate-100 text-slate-500'}`}>
                                                    <Sparkles size={20} />
                                                </div>
                                                {mode === 'ai' && (
                                                    <div className="h-4 w-4 rounded-full bg-slate-900 shadow-sm" />
                                                )}
                                            </div>
                                            <h4 className={`font-semibold ${mode === 'ai' ? 'text-slate-900' : 'text-slate-700'}`}>AI Contextual</h4>
                                            <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                                                Analyzes the current page content to generate a relevant, intelligent greeting automatically.
                                            </p>
                                        </div>

                                        {/* Default Mode Option */}
                                        <div
                                            onClick={() => handleModeChange('default')}
                                            className={`relative cursor-pointer rounded-xl border-2 p-4 transition-all duration-200 ${mode === 'default'
                                                ? 'border-slate-900 bg-slate-900/5'
                                                : 'border-slate-100 bg-white hover:border-slate-200 hover:bg-slate-50'
                                                }`}
                                        >
                                            <div className="flex items-center justify-between mb-3">
                                                <div className={`p-2 rounded-lg ${mode === 'default' ? 'bg-white text-slate-900 shadow-sm' : 'bg-slate-100 text-slate-500'}`}>
                                                    <Type size={20} />
                                                </div>
                                                {mode === 'default' && (
                                                    <div className="h-4 w-4 rounded-full bg-slate-900 shadow-sm" />
                                                )}
                                            </div>
                                            <h4 className={`font-semibold ${mode === 'default' ? 'text-slate-900' : 'text-slate-700'}`}>Custom Message</h4>
                                            <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                                                Set a specific, static message that appears on every page regardless of content.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* 4. Custom Message Input (Conditional) */}
                                {mode === 'default' && (
                                    <div className="space-y-4 pl-8 animate-in fade-in slide-in-from-top-2">
                                        <div className="relative">
                                            <label className="absolute -top-2.5 left-3 px-1 bg-white text-xs font-semibold text-slate-500">
                                                Message Text
                                            </label>
                                            <div className="relative">
                                                <textarea
                                                    value={message}
                                                    onChange={(e) => handleMessageChange(e.target.value)}
                                                    maxLength={100}
                                                    rows={3}
                                                    placeholder="Have a question? Ask me anything!"
                                                    className="w-full p-4 text-sm bg-white border border-slate-200 rounded-xl focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-slate-900 transition-all placeholder:text-slate-300 resize-none"
                                                />
                                                <div className="absolute bottom-3 right-3 text-[10px] bg-slate-100 px-1.5 py-0.5 rounded text-slate-400 font-medium font-mono">
                                                    {message.length}/100
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Preview Note */}
                                <div className="bg-blue-50/50 rounded-xl p-4 border border-blue-100 flex gap-3 items-start">
                                    <MessageCircle size={16} className="text-blue-500 mt-0.5 shrink-0" />
                                    <div className="text-xs text-blue-800 leading-relaxed">
                                        <strong className="font-semibold block mb-0.5 text-blue-900">Visual Preview</strong>
                                        The bubble will slide in from the bottom right after {delay} seconds.
                                        {mode === 'ai'
                                            ? ' Since AI mode is active, the text will vary based on what the user is reading.'
                                            : ' It will display your custom message exactly as written above.'}
                                    </div>
                                </div>

                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

ProactiveBubbleSettings.propTypes = {
    isEditMode: PropTypes.bool,
    updateConfig: PropTypes.func.isRequired,
    proactiveBubbleEnabled: PropTypes.bool,
    setProactiveBubbleEnabled: PropTypes.func,
    proactiveBubbleDelay: PropTypes.number,
    setProactiveBubbleDelay: PropTypes.func,
    proactiveBubbleMode: PropTypes.oneOf(['ai', 'default']),
    setProactiveBubbleMode: PropTypes.func,
    proactiveBubbleDefaultMessage: PropTypes.string,
    setProactiveBubbleDefaultMessage: PropTypes.func,
};

export default ProactiveBubbleSettings;
